import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { isPlatformApiError } from '@traceio-org/platform-client';
import {
  ORGANIZATION_SLUG_PATTERN,
  SUBSCRIBABLE_PRODUCT_KEYS,
  organizationCreateRequestSchema,
  organizationSlugFor,
  type OrganizationCreated,
  type OrganizationKind,
  type SubscribableProductKey,
} from '@traceio-org/platform-contracts';
import { CheckboxGroup, RadioGroup, ReasonField, TextField, reasonError } from '../components/fields';
import { WriteDialog } from '../components/WriteDialog';
import { ORGANIZATIONS_PAGE_SIZE } from '../api';
import { useAdmin } from '../context';
import { productName } from '../format';

/** What the organization's page shows once it opens after New organization (history state, never the URL). */
export interface CreatedNoticeState {
  createdNotice: string;
}

/** The notice of a created organization, from the history state the dialog navigated with (anything else: none). */
export function createdNoticeOf(state: unknown): string | null {
  const notice = (state as Partial<CreatedNoticeState> | null)?.createdNotice;
  return typeof notice === 'string' && notice !== '' ? notice : null;
}

/** What one creation did, in sentences: the notice on the new organization's page. */
export function describeCreated(created: OrganizationCreated): string {
  const parts = [
    `${created.organization.displayName} is created.`,
    `The owner invitation went to ${created.invitation.emailHint}.`,
  ];
  if (created.identityProvider.state === 'failed') {
    parts.push('Linking its Auth0 organization failed: run Link to Auth0 on this page.');
  }
  if (created.enterprise) parts.push('Enterprise is set up once the owner accepts it.');
  return parts.join(' ');
}

const PRODUCT_DESCRIPTIONS: Partial<Record<SubscribableProductKey, string>> = {
  enterprise:
    'Set up in Trace Enterprise once the owner accepts the invitation, with the owner as its administrator.',
  portal: "For the organization's own customers: subscribed, never granted to the owner.",
};

const KIND_CHOICES: { value: OrganizationKind; label: string; description: string }[] = [
  {
    value: 'customer',
    label: 'Customer',
    description: 'A company Trace serves. Billing is set up later on its Billing tab.',
  },
  {
    value: 'internal',
    label: 'Internal',
    description: "Trace's own organization: billing exempt, never billed, with this reason.",
  },
];

interface Form {
  displayName: string;
  slug: string;
  /** True once the slug was typed in: from then on the name no longer suggests it. */
  slugEdited: boolean;
  kind: OrganizationKind;
  products: SubscribableProductKey[];
  ownerEmail: string;
  reason: string;
}

type Errors = Partial<Record<'displayName' | 'slug' | 'ownerEmail' | 'reason' | 'claim', string>>;

const EMPTY: Form = {
  displayName: '',
  slug: '',
  slugEdited: false,
  kind: 'customer',
  products: [],
  ownerEmail: '',
  reason: '',
};

function validate(form: Form, claim: string | null, acknowledged: boolean): Errors {
  const errors: Errors = {};
  const name = form.displayName.trim();
  if (!name) errors.displayName = 'Enter the company name.';
  else if (name.length < 2) errors.displayName = 'Write at least 2 characters.';
  else if (name.length > 120) errors.displayName = 'Write at most 120 characters.';
  if (!form.slug) errors.slug = 'Enter the slug.';
  else if (!ORGANIZATION_SLUG_PATTERN.test(form.slug))
    errors.slug =
      'Use lowercase letters, digits and dashes, starting and ending with a letter or digit (64 at most).';
  const email = form.ownerEmail.trim();
  if (!email) errors.ownerEmail = "Enter the owner's email address.";
  else if (!organizationCreateRequestSchema.shape.ownerEmail.safeParse(email).success)
    errors.ownerEmail = 'Enter a valid email address.';
  const reason = reasonError(form.reason);
  if (reason) errors.reason = reason;
  if (claim && !acknowledged) errors.claim = 'Confirm that you want another organization for this domain.';
  return errors;
}

/**
 * Trace Admin > Organizations > New organization (platform admins): one dialog, one request
 * (POST /v1/admin/organizations), which creates the organization, its Auth0 organization, its products, the owner's
 * invitation and, with Enterprise, its Enterprise setup. When another organization claims the owner's email domain,
 * platform-api answers 409 email_domain_claimed: the dialog shows its words and saves on the second click once
 * "Create it anyway" is checked. After saving, the new organization's page opens with what was done.
 */
export function NewOrganizationDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { api } = useAdmin();
  const navigate = useNavigate();
  const [form, setForm] = useState<Form>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  // The claimed-domain warning platform-api gave for the address in the form, and the confirmation of it.
  const [claim, setClaim] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const created = useRef<OrganizationCreated | null>(null);
  // The organization that already has the slug, when platform-api answered slug_taken and the list found it.
  const [takenBy, setTakenBy] = useState<{ id: string; displayName: string } | null>(null);

  const change = (next: Partial<Form>) => setForm((current) => ({ ...current, ...next }));

  /** The organization that has this exact slug, for a link to it; null when the search cannot say. */
  const organizationWithSlug = async (slug: string) => {
    try {
      const page = await api.listOrganizations({ query: slug, limit: ORGANIZATIONS_PAGE_SIZE });
      const found = page.items.find((organization) => organization.slug === slug);
      return found ? { id: found.id, displayName: found.displayName } : null;
    } catch {
      // Only a convenience: the slug error already says what to do.
      return null;
    }
  };

  return (
    <WriteDialog
      open={open}
      onClose={onClose}
      title="New organization"
      description="Creates the organization, turns on its products and invites its owner. No contract and no card: billing stays on its Billing tab."
      submitLabel="Create the organization"
      onSubmit={async () => {
        const found = validate(form, claim, acknowledged);
        setErrors(found);
        if (Object.keys(found).length > 0) return 'invalid';
        created.current = null;
        try {
          created.current = await api.createOrganization({
            displayName: form.displayName,
            slug: form.slug,
            kind: form.kind,
            products: form.products,
            ownerEmail: form.ownerEmail,
            reason: form.reason,
            acknowledgeDomainClaim: claim !== null && acknowledged,
          });
        } catch (error) {
          const reason =
            isPlatformApiError(error) && error.kind === 'http' && error.status === 409
              ? error.problem.reason
              : undefined;
          // Not a failure: a warning staff confirm, then the same request goes again with the acknowledgement.
          if (reason === 'email_domain_claimed' && isPlatformApiError(error)) {
            setClaim(error.problem.detail ?? 'Another organization claims the email domain of this address.');
            setAcknowledged(false);
            return 'invalid';
          }
          if (reason === 'slug_taken' && isPlatformApiError(error)) {
            setErrors({ slug: error.problem.detail ?? 'Another organization already has this slug.' });
            setTakenBy(await organizationWithSlug(form.slug));
            return 'invalid';
          }
          if (reason === 'self_change') {
            setErrors({
              ownerEmail:
                "This is your own address. Enter the owner's address: Trace staff cannot be invited as an organization's owner.",
            });
            return 'invalid';
          }
          throw error;
        }
        return undefined;
      }}
      onSaved={() => {
        const answer = created.current;
        if (!answer) return;
        const state: CreatedNoticeState = { createdNotice: describeCreated(answer) };
        void navigate(`/organizations/${answer.organization.id}`, { state });
      }}
    >
      <TextField
        label="Company name"
        value={form.displayName}
        onChange={(value) => {
          if (form.slugEdited) {
            change({ displayName: value });
            return;
          }
          change({ displayName: value, slug: organizationSlugFor(value) });
          setTakenBy(null);
        }}
        error={errors.displayName ?? null}
        maxLength={120}
        autoComplete="off"
        required
      />
      <TextField
        label="Slug"
        value={form.slug}
        onChange={(value) => {
          change({ slug: value, slugEdited: true });
          setTakenBy(null);
        }}
        hint="Its address in Trace and the name of its Auth0 organization. Suggested from the name; lowercase letters, digits and dashes."
        error={errors.slug ?? null}
        maxLength={64}
        autoComplete="off"
        spellCheck={false}
        required
      />
      {takenBy ? (
        <p className="admin-note">
          <Link className="admin-link" to={`/organizations/${takenBy.id}`}>
            Open {takenBy.displayName}
          </Link>
        </p>
      ) : null}
      <RadioGroup
        legend="Kind"
        choices={KIND_CHOICES}
        value={form.kind}
        onChange={(value) => change({ kind: value as OrganizationKind })}
      />
      <CheckboxGroup
        legend="Products"
        hint="Each one is turned on without a billing plan, as Add a product does."
        choices={SUBSCRIBABLE_PRODUCT_KEYS.map((key) => ({
          value: key,
          label: productName(key),
          ...(PRODUCT_DESCRIPTIONS[key] ? { description: PRODUCT_DESCRIPTIONS[key] } : {}),
        }))}
        values={form.products}
        onChange={(values) => change({ products: values as SubscribableProductKey[] })}
      />
      <TextField
        label="Owner's email"
        type="email"
        value={form.ownerEmail}
        onChange={(value) => {
          change({ ownerEmail: value });
          // A warning was about the address it was given for; another address is checked again on save.
          setClaim(null);
          setAcknowledged(false);
        }}
        hint="Invited as the organization's owner, with its employee products."
        error={errors.ownerEmail ?? null}
        maxLength={320}
        autoComplete="off"
        spellCheck={false}
        required
      />
      {claim ? (
        <div className="admin-dialog-warning" role="alert">
          <p>{claim}</p>
          <CheckboxGroup
            legend="Email domain"
            choices={[
              {
                value: 'confirm',
                label: 'Create it anyway',
                description: 'For example a subsidiary or a separate pilot of the same company.',
              },
            ]}
            values={acknowledged ? ['confirm'] : []}
            onChange={(values) => setAcknowledged(values.includes('confirm'))}
            error={errors.claim ?? null}
          />
        </div>
      ) : null}
      <ReasonField
        value={form.reason}
        onChange={(value) => change({ reason: value })}
        error={errors.reason ?? null}
      />
    </WriteDialog>
  );
}
